import {
  Technique,
  TechniqueContext,
} from "domain/entities/techniques.entities";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { BackButton } from "ui/pages/app/components/buttons/BackButton";
import { useWindowWidth } from "ui/utils/hooks";
import { Modal, ModalContent } from "@chakra-ui/modal";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { useForm } from "react-hook-form";
import { Inputs } from "ui/pages/inputsType";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { INTextarea } from "ui/pages/app/components/inputs/INTextarea";
import { Trans } from "react-i18next";
import { HiLightBulb, HiOutlineTrash, HiPlus } from "react-icons/hi";
import { formatDuration, parseDuration } from "ui/utils/formatters";
import { selectIsSeanceLoading } from "domain/seances/seances.selectors";
import {
  createPersonalStep,
  updatePersonalStep,
} from "domain/seances/seances.actions";
import { getTechnique } from "domain/techniques/techniques.actions";
import { useEffect, useState } from "react";
import { HorizontalDivider } from "ui/pages/app/components/HorizontalDivider";
import { ConsigneInfos } from "ui/pages/app/components/activities/techniques/ConsignesInfos";
import { i18nKey } from "domain/entities/activities.entities";
import {
  createConsigne,
  deleteConsigne,
  getConsignesByTechniqueId,
  updateConsigne,
} from "domain/consignes/consignes.actions";
import { selectUserId } from "domain/auth/auth.selectors";
import { Consigne } from "domain/entities/consignes.entities";
import { INControlledTimeInput } from "ui/pages/app/components/inputs/INTimeInput";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  context?: TechniqueContext;
  addTechnique?: (
    techniqueId: number,
    context: TechniqueContext
  ) => Promise<{
    success: boolean;
  }>;
  personalStep?: Technique;
  fetchSeance?: (id?: number) => Promise<void>;
}

export function ModalManagePersonalStep({
  isOpen,
  onClose,
  context,
  addTechnique,
  personalStep,
  fetchSeance,
}: IProps) {
  const isLoading = useAppSelector(selectIsSeanceLoading);
  const userId = useAppSelector(selectUserId);
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const [isConsigneOpen, setIsConsigneOpen] = useState(false);
  const [consigne, setConsigne] = useState<Consigne | null>(null);
  const formDefaultValues = {
    title: personalStep ? personalStep.title : "",
    storyline: personalStep ? personalStep.sequences[0]?.storyline : "",
    duration: personalStep ? formatDuration(personalStep.duration) : "",
    consigneTitle: personalStep ? personalStep.consignes?.[0]?.title || "" : "",
    consigneContent: personalStep
      ? personalStep.consignes?.[0]?.content || ""
      : "",
    consigneDuration: personalStep
      ? formatDuration(personalStep.consignes?.[0]?.duration)
      : "",
  };

  const getConsigneData = async () => {
    if (personalStep?.id) {
      const resultConsigne = await dispatch(
        getConsignesByTechniqueId(personalStep.id)
      ).unwrap();
      if (resultConsigne.success && resultConsigne.consignes?.[0]) {
        formManager.setValue(
          "consigneTitle",
          resultConsigne.consignes[0].title || ""
        );
        formManager.setValue(
          "consigneContent",
          resultConsigne.consignes[0].content || ""
        );
        formManager.setValue(
          "consigneDuration",
          formatDuration(resultConsigne.consignes[0].duration) || ""
        );
        setConsigne(resultConsigne.consignes[0] || null);
        setIsConsigneOpen(true);
      } else {
        setConsigne(null);
        setIsConsigneOpen(false);
      }
    }
  };

  useEffect(() => {
    if (personalStep) {
      formManager.setValue("title", personalStep ? personalStep.title : "");
      formManager.setValue(
        "storyline",
        personalStep ? personalStep.sequences[0]?.storyline : ""
      );
      formManager.setValue(
        "duration",
        personalStep ? formatDuration(personalStep.duration) : ""
      );
    }
    if (isOpen) {
      getConsigneData();
    }
  }, [personalStep, isOpen]); //eslint-disable-line

  const formManager = useForm<Inputs>({
    mode: "onSubmit",
    defaultValues: { ...formDefaultValues },
  });

  const { register, formState, handleSubmit, control, reset } = formManager;
  const { errors, isValid } = formState;

  const manageConsignes = async (data: Inputs, techniqueId: number) => {
    if (consigne && consigne.id && !isConsigneOpen) {
      return await dispatch(deleteConsigne(consigne.id)).unwrap();
    }
    if (!isConsigneOpen) {
      return { success: true };
    }
    return consigne && consigne.id
      ? await dispatch(
          updateConsigne({
            title: data.consigneTitle,
            content: data.consigneContent,
            duration: parseDuration(data.consigneDuration),
            id: consigne.id,
            order: 0,
          })
        ).unwrap()
      : await dispatch(
          createConsigne({
            title: data.consigneTitle,
            content: data.consigneContent,
            duration: parseDuration(data.consigneDuration),
            techniqueId,
            order: 0,
            userId,
          })
        ).unwrap();
  };

  const handleFormSubmit = async (data: Inputs) => {
    if (personalStep?.id) {
      const resultConsigneSuccess = (
        await manageConsignes(data, personalStep.id)
      ).success;

      const result = await dispatch(
        updatePersonalStep({
          id: personalStep.id,
          technique: {
            title: data.title,
            storyline: data.storyline,
            ...(data.duration && {
              duration: parseDuration(data.duration),
            }),
            context,
          },
        })
      ).unwrap();

      if (result.success && resultConsigneSuccess) {
        if (fetchSeance) fetchSeance();
        reset();
        onClose();
      }
    } else {
      const result = await dispatch(
        createPersonalStep({
          title: data.title,
          storyline: data.storyline,
          ...(data.duration && { duration: parseDuration(data.duration) }),
          context,
        })
      ).unwrap();
      let resultConsigneSuccess = true;

      if (isConsigneOpen) {
        resultConsigneSuccess = (
          await manageConsignes(data, result.techniqueId)
        ).success;
      }

      if (result.success && result.techniqueId && resultConsigneSuccess) {
        const resultTechnique = await dispatch(
          getTechnique({ id: result.techniqueId })
        ).unwrap();

        if (resultTechnique.success && addTechnique && context) {
          const resultInsert = await addTechnique(
            resultTechnique.technique.id,
            context
          );

          if (resultInsert.success) {
            reset();
            onClose();
          }
        }
      }
    }
  };

  const removeConsigne = () => {
    setIsConsigneOpen(false);
    formManager.resetField("consigneTitle");
    formManager.resetField("consigneContent");
    formManager.resetField("consigneDuration");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalContent>
        <div className="flex min-h-dvh w-full flex-col items-center bg-darkGrey-100 px-2 py-4 md:px-12 md:py-9">
          <div className="w-full">
            <div className="flex h-10 w-full items-center justify-between px-2 md:px-0">
              <BackButton onClick={onClose} hideText={!isDesktop} />
            </div>

            <div className="mt-8 flex w-full justify-center">
              <h4 className="w-fit text-center font-bold text-secondaryGrey-900">
                {personalStep
                  ? translate(`ns1:CreateSeance.UpdateCustomStep`)
                  : translate(`ns1:CreateSeance.AddCustomStep`)}
              </h4>
            </div>
          </div>

          <div className="max-w-[750px]">
            <div className="mt-10 w-full rounded-[20px] bg-white px-4 py-6 md:px-20 md:py-12">
              <div className="flex items-center rounded-[20px] bg-darkGrey-200 px-6 py-4">
                <div className="me-4 h-6 w-6">
                  <HiLightBulb size={24} color="#485585" />
                </div>
                <div className="text-sm font-medium text-darkGrey-800 md:text-base">
                  <p>
                    <Trans
                      i18nKey={
                        "CreateSeance.PersonalStepExplanation" as i18nKey
                      }
                      components={[<span className="font-bold" />]}
                    />
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-8">
                <INInput
                  type="title"
                  error={errors.title}
                  label={translate("ns1:Input.Labels.Title") + "*"}
                  placeholder={translate(
                    "ns1:Input.Placeholders.ExempleTechniqueTitle"
                  )}
                  className="mb-4 md:mb-6"
                  register={{
                    ...register("title", {
                      required: translate("ns1:Input.Errors.Required"),
                    }),
                  }}
                />

                <INTextarea
                  error={errors.storyline}
                  label={translate("ns1:Input.Labels.Description") + "*"}
                  placeholder={translate(
                    "ns1:Input.Placeholders.ExempleTechniqueDescription"
                  )}
                  className="mb-4 md:mb-6"
                  register={{
                    ...register("storyline", {
                      required: translate("ns1:Input.Errors.Required"),
                    }),
                  }}
                />

                <INControlledTimeInput
                  control={control}
                  inputName="duration"
                  label={translate("ns1:Input.Labels.Duration")}
                />

                {/* --- CONSIGNE --- */}

                {!isConsigneOpen ? (
                  <div className="mt-10 flex items-center justify-center text-darkGrey-800">
                    <button
                      className="me-4 flex items-center space-x-2"
                      onClick={() => setIsConsigneOpen(true)}
                    >
                      <HiPlus size={20} />
                      <span className="text-sm font-bold">
                        {translate("ns1:TechniqueConsignes.AddConsigne")}
                      </span>
                    </button>
                    <ConsigneInfos />
                  </div>
                ) : (
                  <div>
                    <HorizontalDivider gap="32px" />

                    <h4 className="mb-8 flex items-center font-bold">
                      {translate(
                        "ns1:TechniqueConsignes.ConsigneForParticipants"
                      )}
                      <ConsigneInfos className="ms-2" />
                    </h4>
                    <INInput
                      type="consigneTitle"
                      error={errors.consigneTitle}
                      label={translate("ns1:Input.Labels.Title") + "*"}
                      placeholder={translate(
                        "ns1:Input.Placeholders.ExempleTechniqueConsigneTitle"
                      )}
                      className="mb-4 md:mb-6"
                      register={{
                        ...register("consigneTitle", {
                          required: translate("ns1:Input.Errors.Required"),
                        }),
                      }}
                    />

                    <INTextarea
                      error={errors.consigneContent}
                      label={translate("ns1:Input.Labels.Description") + "*"}
                      placeholder={translate(
                        "ns1:Input.Placeholders.ExempleTechniqueConsigneDescription"
                      )}
                      className="mb-4 md:mb-6"
                      register={{
                        ...register("consigneContent", {
                          required: translate("ns1:Input.Errors.Required"),
                        }),
                      }}
                    />

                    {/* <MDXEditor
                      markdown={formManager.getValues("consigneContent") || ""}
                      plugins={[
                        headingsPlugin(),
                        listsPlugin(),
                        toolbarPlugin({
                          toolbarContents: () => (
                            <>
                              <BoldItalicUnderlineToggles />
                              <ListsToggle options={["bullet", "number"]} />
                            </>
                          ),
                        }),
                      ]}
                      onChange={(value) =>
                        formManager.setValue("consigneContent", value)
                      }
                      className="mb-4 mt-2 rounded-xl border border-darkGrey-500 bg-white p-3 text-sm font-medium text-darkGrey-700 md:mb-6"
                      placeholder={translate(
                        "ns1:Input.Placeholders.ExempleTechniqueConsigneDescription"
                      )}
                    /> */}

                    <INControlledTimeInput
                      inputName="consigneDuration"
                      control={control}
                      label={translate("ns1:Input.Labels.Duration") + "*"}
                      required
                    />

                    <div className="mt-10 flex items-center justify-center text-darkGrey-800">
                      <button
                        className="me-4 flex items-center space-x-2"
                        onClick={removeConsigne}
                        type="button"
                      >
                        <HiOutlineTrash size={20} />
                        <span className="text-sm font-bold">
                          {translate("ns1:TechniqueConsignes.RemoveConsigne")}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 flex w-full flex-col items-center bg-white px-6 py-4 shadow-lg">
          {!isValid && (
            <p className="text-medium mb-4 text-sm text-darkGrey-700">
              *{translate("ns1:Input.Errors.CompleteAllFieldsToValidate")}
            </p>
          )}
          <PrimaryButton
            height="46px"
            width="100%"
            disabled={isLoading || !isValid}
            onClick={handleSubmit(handleFormSubmit)}
            className="max-w-[590px]"
          >
            {translate("ns1:Button.Validate")}
          </PrimaryButton>
        </div>
      </ModalContent>
    </Modal>
  );
}
